/**
 * The Deal Room section shell — the report stops being one long scroll and becomes a document you can
 * navigate.
 *
 * ## The problem this solves
 *
 * A completed deal renders ten-plus stacked cards: hero, metrics, sensitivity, lender, four narratives,
 * data quality, sources, challenges. Read top-to-bottom that is several screens of scrolling before a
 * client reaches the thing they opened the deal to see, and there is no way to send someone to one part
 * of it. Long-scroll is fine for a page you read once; it is wrong for a document a client returns to.
 *
 * ## How it works
 *
 * Sections become panels behind a sticky section bar. Three properties are load-bearing:
 *
 *   • **Every panel is always in the DOM** — inactive ones carry `hidden`, they are not unmounted. So
 *     browser find-in-page works across the whole deal, screen readers can reach it, and nothing has to
 *     re-render (or re-fetch) when the reader moves between sections.
 *   • **Print expands everything.** A tabbed document that prints as one tab is a broken export. The
 *     print stylesheet overrides `hidden` so a PDF carries the entire deal, section headings and all.
 *   • **Sections are deep-linkable** via the URL hash, so "look at the sensitivity" is a link.
 *
 * A section with nothing to show is never rendered as an empty tab — the caller passes what exists and
 * {@link buildSections} drops the rest.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { HelpHint } from './HelpHint.js';
import { helpFor as help } from './section-help.js';

export interface ReportSection {
  /** Stable id — also the URL hash, so it is part of the product's link surface. Keep it kebab-case. */
  id: string;
  label: string;
  /** An optional count badge (open challenges, unread sources) — the reason to look before clicking. */
  badge?: number;
  content: ReactNode;
}

/** A section the caller may or may not have content for. */
export interface CandidateSection extends Omit<ReportSection, 'content'> {
  content: ReactNode | null;
  /** Explicitly drop the section even when `content` is non-null. */
  when?: boolean;
}

/**
 * Keep the sections that actually have something to show. Exported and pure because the emptiness rule
 * is the one that decides what a reader sees: a tab that opens onto nothing is worse than no tab, and a
 * deal missing its lender summary should not advertise a Lender section.
 */
export function buildSections(candidates: CandidateSection[]): ReportSection[] {
  return candidates
    .filter((c) => c.when !== false && c.content !== null && c.content !== undefined)
    .map(({ id, label, badge, content }) => ({
      id,
      label,
      ...(badge !== undefined ? { badge } : {}),
      content: content as ReactNode,
    }));
}

/**
 * The section the URL is asking for, or the first one. Exported for a direct test: a stale or hand-typed
 * hash must fall back to a real section rather than rendering a blank document.
 */
export function sectionFromHash(hash: string, sections: ReportSection[]): string {
  const wanted = hash.replace(/^#/, '');
  return sections.some((s) => s.id === wanted) ? wanted : (sections[0]?.id ?? '');
}

export function ReportSections({ sections }: { sections: ReportSection[] }) {
  const [active, setActive] = useState(() =>
    sectionFromHash(typeof window === 'undefined' ? '' : window.location.hash, sections),
  );
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Follow the hash — so a pasted link, the back button, and an in-page anchor all land on the section
  // the reader asked for rather than silently showing the first one.
  useEffect(() => {
    const onHash = (): void => setActive(sectionFromHash(window.location.hash, sections));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [sections]);

  const select = useCallback((id: string) => {
    setActive(id);
    // `replaceState`, not a hash assignment: switching section is not a navigation the reader should
    // have to press Back through six times to escape.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${id}`);
    }
  }, []);

  // Arrow keys move between sections (the tablist contract) — a keyboard reader should not have to Tab
  // through a whole panel to reach the next section.
  const onKeyDown = (e: React.KeyboardEvent, index: number): void => {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = (index + delta + sections.length) % sections.length;
    const section = sections[next];
    if (!section) return;
    select(section.id);
    tabsRef.current[next]?.focus();
  };

  const current = sections.some((s) => s.id === active) ? active : (sections[0]?.id ?? '');

  return (
    <div className="rpt">
      <div className="rpt__bar" role="tablist" aria-label="Deal sections">
        {sections.map((s, i) => (
          <button
            key={s.id}
            ref={(el) => {
              tabsRef.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`rpt-tab-${s.id}`}
            aria-controls={`rpt-panel-${s.id}`}
            aria-selected={s.id === current}
            tabIndex={s.id === current ? 0 : -1}
            className={`rpt__tab${s.id === current ? ' rpt__tab--on' : ''}`}
            onClick={() => select(s.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {s.label}
            {s.badge !== undefined && s.badge > 0 && <span className="rpt__badge">{s.badge}</span>}
          </button>
        ))}
      </div>

      {sections.map((s) => (
        <section
          key={s.id}
          role="tabpanel"
          id={`rpt-panel-${s.id}`}
          aria-labelledby={`rpt-tab-${s.id}`}
          hidden={s.id !== current}
          className="rpt__panel"
        >
          {/*
            The section's own heading, carrying its help affordance. It is anchored HERE rather than on
            the tab for two reasons: a popover hung off a tab in a horizontally scrolling bar ends up
            off-screen on a phone, and a reader asks "what am I looking at" once they are already
            looking at it. In print, where the tab bar is gone, this is the heading that names the
            section — so it earns its place twice.
          */}
          <div className="rpt__head">
            <h2 className="rpt__title">{s.label}</h2>
            {help(s.id) && <HelpHint label={s.label} text={help(s.id) ?? ''} />}
          </div>
          {s.content}
        </section>
      ))}
    </div>
  );
}
